// src/pages/FeeConfigDashboard.jsx
import React, { useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import API from "../../../services/API";

// ==================== API HELPERS ====================
const fetchFeeConfigs = (params = {}) =>
  API.get("/paymentFee/school/payment-config/list", {
    headers: { token: localStorage.getItem("token") },
    params,
  });

const createFeeConfig = (payload) =>
  API.post("/paymentFee/school/payment-config/create", payload, {
    headers: { token: localStorage.getItem("token") },
  });

const deleteFeeConfig = (configId) =>
  API.delete(`/paymentFee/school/payment-config/${configId}/delete`, {
    headers: { token: localStorage.getItem("token") },
  });

// ==================== DEFAULT FORM ====================
const defaultForm = {
  title: "",
  description: "",
  payment_type: "fee",
  payer_scope: "student",

  amount_type: "fixed",
  fixed_amount: "",
  min_amount: "",
  max_amount: "",

  allow_fiuu: 1,
  allow_manual_upload: 0,

  target_scope: "school",

  class_ids: [],
  forms: [],
  student_ids: [],

  markup_type: "none",
  markup_value: "",
};

// ==================== COMPONENT ====================
const FeeConfigDashboard = () => {
  const navigate = useNavigate();

  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);

  const [classes, setClasses] = useState([]);
  const [uniqueForms, setUniqueForms] = useState([]);
  const [incompleteClasses, setIncompleteClasses] = useState([]);

  const [schoolFeePercent, setSchoolFeePercent] = useState(0);
  const [form, setForm] = useState(defaultForm);

  // ==================== LOAD DATA ====================
  const loadConfigs = async () => {
    setLoading(true);

    try {
      const res = await fetchFeeConfigs();

      if (res.data.response_code === 1000) {
        setConfigs(res.data.data.configs || []);
      } else {
        toast.error(res.data.message || "Failed to load fee configs");
      }
    } catch {
      toast.error("Failed to fetch fee configurations");
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = () => {
    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      token: localStorage.getItem("token"),
    };

    API.get("/portal/getClasses", { headers })
      .then((response) => {
        if (response.data.response_code === 1000) {
          const fetchedClasses = response.data.data || [];

          setClasses(fetchedClasses);

          const formsFromAPI = Array.from(
            new Set(
              fetchedClasses
                .map((c) => c.tingkatan_moeis?.toUpperCase().trim())
                .filter(Boolean)
            )
          );

          const missing = fetchedClasses.filter(
            (c) => !c.tingkatan_moeis || !c.class_moeis_format
          );

          setIncompleteClasses(missing);
          setUniqueForms(formsFromAPI);
        } else {
          toast.error(response.data.message || "Failed to fetch classes");
        }
      })
      .catch(() => {
        toast.error("Error fetching classes");
      });
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  // ==================== COMPUTED DATA ====================
  const activeConfigs = useMemo(
    () => configs.filter((c) => Number(c.status) === 1).length,
    [configs]
  );

  const onlinePaymentConfigs = useMemo(
    () => configs.filter((c) => Number(c.allow_fiuu) === 1).length,
    [configs]
  );

  const manualPaymentConfigs = useMemo(
    () => configs.filter((c) => Number(c.allow_manual_upload) === 1).length,
    [configs]
  );

  const groupedClasses = useMemo(() => {
    return classes.reduce((acc, c) => {
      const formLevel = (c.tingkatan_moeis || "OTHER").toUpperCase();

      if (!acc[formLevel]) acc[formLevel] = [];

      acc[formLevel].push(c);

      return acc;
    }, {});
  }, [classes]);

  const getAmountLabel = (c) => {
    if (c.amount_type === "fixed") {
      return `RM ${Number(c.fixed_amount || 0).toFixed(2)}`;
    }

    if (c.amount_type === "range") {
      return `RM ${Number(c.min_amount || 0).toFixed(2)} – RM ${Number(
        c.max_amount || 0
      ).toFixed(2)}`;
    }

    if (c.amount_type === "flexible") {
      return c.min_amount
        ? `Min RM ${Number(c.min_amount || 0).toFixed(2)}`
        : "Flexible";
    }

    return "Variable";
  };

  const getFeePreview = () => {
    const amount =
      form.amount_type === "fixed" ? form.fixed_amount : form.min_amount;

    if (!amount) return null;

    const base = Number(amount || 0);
    const schoolFee = (base * Number(schoolFeePercent || 0)) / 100;
    const subtotal = base + schoolFee;

    let markup = 0;

    if (form.markup_type === "percent") {
      markup = (subtotal * Number(form.markup_value || 0)) / 100;
    }

    if (form.markup_type === "fixed") {
      markup = Number(form.markup_value || 0);
    }

    const total = subtotal + markup;

    return {
      base,
      schoolFee,
      markup,
      total,
    };
  };

  // ==================== FORM HELPERS ====================
  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleClass = (classId, checked) => {
    setForm((prev) => ({
      ...prev,
      class_ids: checked
        ? [...prev.class_ids, classId]
        : prev.class_ids.filter((id) => id !== classId),
    }));
  };

  const openCreateModal = () => {
    fetchClasses();
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
  };

  // ==================== ACTIONS ====================
  const handleDeleteConfig = async (config) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${config.title}"?\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      const res = await deleteFeeConfig(config.config_id);

      if (res.data.response_code === 1000) {
        toast.success("Fee configuration deleted");
        loadConfigs();
      } else {
        toast.error(res.data.message || "Failed to delete fee");
      }
    } catch {
      toast.error("Failed to delete fee configuration");
    }
  };

  const handleCreateFee = async () => {
    if (!form.title.trim()) {
      return toast.error("Fee title is required");
    }

    if (form.amount_type === "fixed" && !form.fixed_amount) {
      return toast.error("Fixed amount is required");
    }

    if (form.amount_type === "flexible" && !form.min_amount) {
      return toast.error("Minimum amount is required");
    }

    if (form.target_scope === "class" && form.class_ids.length === 0) {
      return toast.error("Please select at least one class");
    }

    setCreating(true);

    try {
      const payload = {
        ...form,
        fixed_amount:
          form.amount_type === "fixed" ? Number(form.fixed_amount) : null,
        min_amount:
          form.amount_type === "flexible" ? Number(form.min_amount) : null,
        max_amount: null,
        markup_value: form.markup_value ? Number(form.markup_value) : 0,
      };

      const res = await createFeeConfig(payload);

      if (res.data.response_code === 1000) {
        toast.success("Fee created");
        setShowCreateModal(false);
        setForm(defaultForm);
        loadConfigs();
      } else {
        toast.error(res.data.message || "Failed to create fee");
      }
    } catch {
      toast.error("Failed to create fee");
    } finally {
      setCreating(false);
    }
  };

  // ==================== RENDER ====================
  return (
    <Fragment>
      <div className="container-fluid py-4" style={{ background: "#f8fafc", minHeight: "100vh" }}>
        {/* Header */}
        <div
          className="rounded-5 p-4 p-md-5 mb-4 position-relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, #7f1d1d 0%, #dc2626 48%, #fb7185 100%)",
            boxShadow: "0 24px 60px rgba(220, 38, 38, 0.18)",
          }}
        >
          <div
            className="position-absolute top-0 end-0 translate-middle-y rounded-circle"
            style={{
              width: 260,
              height: 260,
              background: "rgba(255,255,255,0.12)",
              filter: "blur(2px)",
            }}
          />

          <div
            className="position-absolute bottom-0 start-50 rounded-circle"
            style={{
              width: 180,
              height: 180,
              background: "rgba(255,255,255,0.10)",
              filter: "blur(1px)",
            }}
          />

          <div className="position-relative d-flex flex-column flex-lg-row justify-content-between gap-4">
            <div>
              <div
                className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-3"
                style={{
                  background: "rgba(255,255,255,0.16)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.24)",
                  backdropFilter: "blur(10px)",
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                }}
              >
                <span
                  className="rounded-circle"
                  style={{
                    width: 8,
                    height: 8,
                    background: "#fff",
                    display: "inline-block",
                  }}
                />
                Payment Management
              </div>

              <h1 className="text-white fw-black mb-2" style={{ fontSize: "clamp(32px, 4vw, 52px)", letterSpacing: "-1.5px" }}>
                Fee Configuration
              </h1>

              <p className="text-white-50 mb-0" style={{ maxWidth: 680, fontSize: 16 }}>
                Create, manage, and monitor school fee structures across the
                entire school, selected classes, or individual students.
              </p>
            </div>

            <div className="d-flex align-items-start">
              <button
                className="btn border-0 fw-bold px-4 py-3 rounded-4"
                style={{
                  background: "#fff",
                  color: "#991b1b",
                  boxShadow: "0 18px 40px rgba(0,0,0,0.12)",
                }}
                onClick={openCreateModal}
              >
                + Add Fee Configuration
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="card border-0 rounded-5 shadow-sm h-100">
              <div className="card-body p-4">
                <div className="text-muted small fw-semibold mb-2">Total Fees</div>
                <div className="display-6 fw-black text-dark">{configs.length}</div>
                <div className="text-muted small mt-2">Configurations created</div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-0 rounded-5 shadow-sm h-100">
              <div className="card-body p-4">
                <div className="text-muted small fw-semibold mb-2">Active</div>
                <div className="display-6 fw-black text-success">{activeConfigs}</div>
                <div className="text-muted small mt-2">Currently enabled</div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-0 rounded-5 shadow-sm h-100">
              <div className="card-body p-4">
                <div className="text-muted small fw-semibold mb-2">Online Payment</div>
                <div className="display-6 fw-black text-primary">{onlinePaymentConfigs}</div>
                <div className="text-muted small mt-2">Online enabled</div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-0 rounded-5 shadow-sm h-100">
              <div className="card-body p-4">
                <div className="text-muted small fw-semibold mb-2">Manual Upload</div>
                <div className="display-6 fw-black text-warning">{manualPaymentConfigs}</div>
                <div className="text-muted small mt-2">Receipt upload allowed</div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Table Card */}
        <div className="card border-0 shadow-sm rounded-5 overflow-hidden">
          <div className="card-header bg-white border-0 p-4">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
              <div>
                <h5 className="fw-black mb-1 text-dark">Configured Fees</h5>
                <div className="text-muted small">
                  Manage fee collections, payment methods, and payer visibility.
                </div>
              </div>

              <button
                className="btn btn-outline-danger rounded-4 fw-semibold px-4"
                onClick={loadConfigs}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table align-middle mb-0" style={{ minWidth: 980 }}>
              <thead>
                <tr style={{ background: "#f8fafc" }}>
                  <th className="border-0 text-muted small fw-bold px-4 py-3">Fee</th>
                  <th className="border-0 text-muted small fw-bold px-4 py-3">Amount</th>
                  <th className="border-0 text-muted small fw-bold px-4 py-3">Scope</th>
                  <th className="border-0 text-muted small fw-bold px-4 py-3">Methods</th>
                  <th className="border-0 text-muted small fw-bold px-4 py-3">Status</th>
                  <th className="border-0 text-muted small fw-bold px-4 py-3 text-end">Action</th>
                </tr>
              </thead>

              <tbody>
                {loading && configs.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="spinner-border text-danger mb-3" role="status" />
                      <div className="fw-semibold text-dark">Loading fee configurations</div>
                      <div className="text-muted small">Please wait while we fetch your data.</div>
                    </td>
                  </tr>
                )}

                {!loading &&
                  configs.map((c) => (
                    <tr key={c.config_id} style={{ borderTop: "1px solid #eef2f7" }}>
                      <td className="px-4 py-4">
                        <div className="d-flex align-items-start gap-3">
                          <div
                            className="rounded-4 d-flex align-items-center justify-content-center fw-bold text-danger"
                            style={{
                              width: 46,
                              height: 46,
                              background: "#fee2e2",
                              flex: "0 0 auto",
                            }}
                          >
                            RM
                          </div>

                          <div>
                            <div className="fw-bold text-dark mb-1">{c.title}</div>
                            <div
                              className="text-muted small"
                              style={{
                                maxWidth: 420,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {c.description || "No description provided"}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <div className="fw-bold text-dark">{getAmountLabel(c)}</div>
                        <div className="text-muted small text-capitalize">
                          {c.amount_type || "variable"} amount
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <span
                          className="badge rounded-pill px-3 py-2 text-capitalize"
                          style={{
                            background: "#f1f5f9",
                            color: "#334155",
                            fontWeight: 700,
                          }}
                        >
                          {c.payer_scope || "student"}
                        </span>
                      </td>

                      <td className="px-4 py-4">
                        <div className="d-flex flex-wrap gap-2">
                          {Number(c.allow_fiuu) === 1 && (
                            <span className="badge rounded-pill bg-primary-subtle text-primary px-3 py-2">
                              Online payment
                            </span>
                          )}

                          {Number(c.allow_manual_upload) === 1 && (
                            <span className="badge rounded-pill bg-warning-subtle text-warning-emphasis px-3 py-2">
                              Manual
                            </span>
                          )}

                          {Number(c.allow_fiuu) !== 1 &&
                            Number(c.allow_manual_upload) !== 1 && (
                              <span className="text-muted small">No method</span>
                            )}
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        {Number(c.status) === 1 ? (
                          <span className="badge rounded-pill bg-success-subtle text-success px-3 py-2">
                            Active
                          </span>
                        ) : (
                          <span className="badge rounded-pill bg-danger-subtle text-danger px-3 py-2">
                            Inactive
                          </span>
                        )}
                      </td>

                      <td className="px-4 py-4">
                        <div className="d-flex justify-content-end gap-2">
                          <button
                            className="btn btn-sm btn-light rounded-4 fw-semibold px-3"
                            onClick={() =>
                              navigate(`/feeConfigDashboard/${c.config_id}/payers`)
                            }
                          >
                            View Payers
                          </button>

                          <button
                            className="btn btn-sm btn-outline-danger rounded-4 fw-semibold px-3"
                            onClick={() => handleDeleteConfig(c)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                {!loading && configs.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div
                        className="mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: 70,
                          height: 70,
                          background: "#fee2e2",
                          color: "#dc2626",
                          fontWeight: 900,
                          fontSize: 24,
                        }}
                      >
                        RM
                      </div>

                      <div className="fw-bold text-dark mb-1">No fee configurations yet</div>
                      <div className="text-muted small mb-4">
                        Create your first fee configuration to start collecting payments.
                      </div>

                      <button
                        className="btn btn-danger rounded-4 fw-bold px-4"
                        onClick={openCreateModal}
                      >
                        + Add Fee Configuration
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ==================== CREATE MODAL ==================== */}
      {showCreateModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content border-0 rounded-5 overflow-hidden shadow-lg">
                <div
                  className="modal-header border-0 p-4"
                  style={{
                    background: "linear-gradient(135deg, #991b1b, #dc2626)",
                  }}
                >
                  <div>
                    <div className="text-white-50 small fw-semibold mb-1">
                      New Payment Collection
                    </div>
                    <h5 className="modal-title fw-black text-white mb-0">
                      Create Fee Configuration
                    </h5>
                  </div>

                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={closeCreateModal}
                  />
                </div>

                <div className="modal-body p-4" style={{ background: "#f8fafc" }}>
                  <div className="row g-4">
                    {/* Left Form */}
                    <div className="col-lg-8">
                      <div className="card border-0 rounded-5 shadow-sm">
                        <div className="card-body p-4">
                          <div className="mb-4">
                            <h6 className="fw-black text-dark mb-1">Fee Details</h6>
                            <div className="text-muted small">
                              Set the fee name, amount rules, and who should pay.
                            </div>
                          </div>

                          <div className="row g-3">
                            <div className="col-md-12">
                              <label className="form-label fw-semibold small text-muted">
                                Fee Title
                              </label>
                              <input
                                className="form-control form-control-lg rounded-4"
                                placeholder="Example: Term 1 School Fee"
                                value={form.title}
                                onChange={(e) => updateForm("title", e.target.value)}
                              />
                            </div>

                            <div className="col-12">
                              <label className="form-label fw-semibold small text-muted">
                                Description
                              </label>
                              <textarea
                                className="form-control rounded-4"
                                rows="3"
                                placeholder="Optional notes for parents or internal reference"
                                value={form.description}
                                onChange={(e) =>
                                  updateForm("description", e.target.value)
                                }
                              />
                            </div>

                            <div className="col-md-4">
                              <label className="form-label fw-semibold small text-muted">
                                Target Scope
                              </label>
                              <select
                                className="form-select rounded-4"
                                value={form.target_scope}
                                onChange={(e) =>
                                  updateForm("target_scope", e.target.value)
                                }
                              >
                                <option value="school">Entire School</option>
                                <option value="class">Selected Classes</option>
                                <option value="student">Specific Students</option>
                              </select>
                            </div>

                            <div className="col-md-4">
                              <label className="form-label fw-semibold small text-muted">
                                Payer Scope
                              </label>
                              <select
                                className="form-select rounded-4"
                                value={form.payer_scope}
                                onChange={(e) =>
                                  updateForm("payer_scope", e.target.value)
                                }
                              >
                                <option value="student">Student</option>
                                <option value="parent">Parent</option>
                              </select>
                            </div>

                            <div className="col-md-4">
                              <label className="form-label fw-semibold small text-muted">
                                Amount Type
                              </label>
                              <select
                                className="form-select rounded-4"
                                value={form.amount_type}
                                onChange={(e) =>
                                  updateForm("amount_type", e.target.value)
                                }
                              >
                                <option value="fixed">Fixed Amount</option>
                                <option value="flexible">
                                  Flexible Minimum Amount
                                </option>
                              </select>
                            </div>

                            {form.amount_type === "fixed" && (
                              <div className="col-md-6">
                                <label className="form-label fw-semibold small text-muted">
                                  Fixed Amount (RM)
                                </label>
                                <input
                                  type="number"
                                  className="form-control form-control-lg rounded-4"
                                  placeholder="0.00"
                                  value={form.fixed_amount}
                                  onChange={(e) =>
                                    updateForm("fixed_amount", e.target.value)
                                  }
                                />
                              </div>
                            )}

                            {form.amount_type === "flexible" && (
                              <div className="col-md-6">
                                <label className="form-label fw-semibold small text-muted">
                                  Minimum Amount (RM)
                                </label>
                                <input
                                  type="number"
                                  className="form-control form-control-lg rounded-4"
                                  placeholder="0.00"
                                  value={form.min_amount}
                                  onChange={(e) =>
                                    updateForm("min_amount", e.target.value)
                                  }
                                />
                              </div>
                            )}

                            <div className="col-md-6">
                              <label className="form-label fw-semibold small text-muted">
                                Payment Methods
                              </label>

                              <div className="d-flex flex-column gap-2">
                                <label className="border rounded-4 px-3 py-3 d-flex align-items-center justify-content-between bg-white">
                                  <div>
                                    <div className="fw-bold text-dark">
                                      Online Payment
                                    </div>
                                    <div className="text-muted small">
                                      Allow payment gateway collection
                                    </div>
                                  </div>

                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={Number(form.allow_fiuu) === 1}
                                    onChange={(e) =>
                                      updateForm("allow_fiuu", e.target.checked ? 1 : 0)
                                    }
                                  />
                                </label>

                                <label className="border rounded-4 px-3 py-3 d-flex align-items-center justify-content-between bg-white">
                                  <div>
                                    <div className="fw-bold text-dark">
                                      Manual Upload
                                    </div>
                                    <div className="text-muted small">
                                      Allow receipt upload by payer
                                    </div>
                                  </div>

                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={Number(form.allow_manual_upload) === 1}
                                    onChange={(e) =>
                                      updateForm(
                                        "allow_manual_upload",
                                        e.target.checked ? 1 : 0
                                      )
                                    }
                                  />
                                </label>
                              </div>
                            </div>

                            {form.target_scope === "student" && (
                              <div className="col-12">
                                <label className="form-label fw-semibold small text-muted">
                                  Student UUIDs
                                </label>
                                <input
                                  className="form-control rounded-4"
                                  placeholder="uuid1, uuid2, uuid3"
                                  value={form.student_ids.join(", ")}
                                  onChange={(e) =>
                                    updateForm(
                                      "student_ids",
                                      e.target.value
                                        .split(",")
                                        .map((s) => s.trim())
                                        .filter(Boolean)
                                    )
                                  }
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {form.target_scope === "class" && (
                        <div className="card border-0 rounded-5 shadow-sm mt-4">
                          <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
                              <div>
                                <h6 className="fw-black text-dark mb-1">
                                  Select Classes
                                </h6>
                                <div className="text-muted small">
                                  Choose which classes should receive this fee.
                                </div>
                              </div>

                              <span className="badge rounded-pill bg-danger-subtle text-danger px-3 py-2">
                                {form.class_ids.length} selected
                              </span>
                            </div>

                            <div
                              className="rounded-4 border bg-white p-3"
                              style={{ maxHeight: 360, overflowY: "auto" }}
                            >
                              {Object.keys(groupedClasses).length === 0 && (
                                <div className="text-center text-muted py-4">
                                  No classes found
                                </div>
                              )}

                              {Object.keys(groupedClasses).map((formLevel) => (
                                <div key={formLevel} className="mb-4">
                                  <div className="d-flex align-items-center gap-2 mb-3">
                                    <div
                                      className="rounded-pill px-3 py-1 fw-bold small"
                                      style={{
                                        background: "#fee2e2",
                                        color: "#991b1b",
                                      }}
                                    >
                                      {formLevel}
                                    </div>

                                    <div className="text-muted small">
                                      {groupedClasses[formLevel].length} classes
                                    </div>
                                  </div>

                                  <div className="row g-2">
                                    {groupedClasses[formLevel].map((c) => {
                                      const selected = form.class_ids.includes(
                                        c.class_id
                                      );

                                      return (
                                        <div
                                          key={c.class_id}
                                          className="col-md-4 col-sm-6"
                                        >
                                          <label
                                            className="w-100 rounded-4 border px-3 py-3 d-flex align-items-center gap-3"
                                            style={{
                                              cursor: "pointer",
                                              background: selected
                                                ? "#fff1f2"
                                                : "#fff",
                                              borderColor: selected
                                                ? "#fecdd3"
                                                : "#e2e8f0",
                                            }}
                                          >
                                            <input
                                              type="checkbox"
                                              className="form-check-input m-0"
                                              checked={selected}
                                              onChange={(e) =>
                                                toggleClass(
                                                  c.class_id,
                                                  e.target.checked
                                                )
                                              }
                                            />

                                            <div>
                                              <div className="fw-bold text-dark small">
                                                {c.class_moeis_format ||
                                                  c.class_name}
                                              </div>
                                              <div className="text-muted small">
                                                Class ID: {c.class_id}
                                              </div>
                                            </div>
                                          </label>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {incompleteClasses.length > 0 && (
                              <div className="alert alert-warning rounded-4 mt-3 mb-0 small">
                                {incompleteClasses.length} class records have
                                incomplete MOEIS format or form information.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Preview */}
                    <div className="col-lg-4">
                      <div className="card border-0 rounded-5 shadow-sm sticky-top" style={{ top: 20 }}>
                        <div className="card-body p-4">
                          <div className="mb-4">
                            <h6 className="fw-black text-dark mb-1">
                              Payment Preview
                            </h6>
                            <div className="text-muted small">
                              Review how this fee will appear before creating it.
                            </div>
                          </div>

                          <div
                            className="rounded-5 p-4 mb-3"
                            style={{
                              background:
                                "linear-gradient(135deg, #111827, #7f1d1d)",
                              color: "#fff",
                            }}
                          >
                            <div className="text-white-50 small mb-2">
                              Total Payable
                            </div>

                            <div className="fw-black mb-1" style={{ fontSize: 34 }}>
                              {(() => {
                                const preview = getFeePreview();
                                return preview
                                  ? `RM ${preview.total.toFixed(2)}`
                                  : "RM 0.00";
                              })()}
                            </div>

                            <div className="text-white-50 small">
                              {form.title || "Untitled fee configuration"}
                            </div>
                          </div>

                          {(() => {
                            const preview = getFeePreview();

                            return (
                              <div className="d-flex flex-column gap-3">
                                <div className="d-flex justify-content-between">
                                  <span className="text-muted small">Base Amount</span>
                                  <span className="fw-bold">
                                    RM {preview ? preview.base.toFixed(2) : "0.00"}
                                  </span>
                                </div>

                                <div className="d-flex justify-content-between">
                                  <span className="text-muted small">
                                    School Fee
                                  </span>
                                  <span className="fw-bold">
                                    RM{" "}
                                    {preview
                                      ? preview.schoolFee.toFixed(2)
                                      : "0.00"}
                                  </span>
                                </div>

                                <div className="d-flex justify-content-between">
                                  <span className="text-muted small">Markup</span>
                                  <span className="fw-bold">
                                    RM {preview ? preview.markup.toFixed(2) : "0.00"}
                                  </span>
                                </div>

                                <hr />

                                <div className="d-flex justify-content-between">
                                  <span className="text-muted small">Charged To</span>
                                  <span className="fw-bold text-capitalize">
                                    {form.payer_scope}
                                  </span>
                                </div>

                                <div className="d-flex justify-content-between">
                                  <span className="text-muted small">Target</span>
                                  <span className="fw-bold text-capitalize">
                                    {form.target_scope}
                                  </span>
                                </div>

                                <div className="d-flex justify-content-between">
                                  <span className="text-muted small">Amount Type</span>
                                  <span className="fw-bold text-capitalize">
                                    {form.amount_type}
                                  </span>
                                </div>

                                <div className="pt-2">
                                  <div className="text-muted small mb-2">
                                    Payment Methods
                                  </div>

                                  <div className="d-flex flex-wrap gap-2">
                                    {Number(form.allow_fiuu) === 1 && (
                                      <span className="badge rounded-pill bg-primary-subtle text-primary px-3 py-2">
                                        Online
                                      </span>
                                    )}

                                    {Number(form.allow_manual_upload) === 1 && (
                                      <span className="badge rounded-pill bg-warning-subtle text-warning-emphasis px-3 py-2">
                                        Manual
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer border-0 bg-white p-4">
                  <button
                    className="btn btn-light rounded-4 fw-semibold px-4"
                    onClick={closeCreateModal}
                    disabled={creating}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-danger rounded-4 fw-bold px-4"
                    disabled={creating}
                    onClick={handleCreateFee}
                  >
                    {creating ? "Creating..." : "Create Fee"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show" />
        </>
      )}
    </Fragment>
  );
};

export default FeeConfigDashboard;